/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';

import { WelcomeAnimation } from '../../../../common/ui/components/welcome-animation';
import { fetchUserName } from '../../../../services/user-data';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';

import { messages } from './messages';

const styles = cssMap({
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.100'),
		textAlign: 'center',
		paddingTop: token('space.200'),
		opacity: 0,
		transform: 'translateY(20px)',
		transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		maxWidth: '100%',
		'@media (min-width: 64rem)': {
			marginTop: token('space.1000'),
		},
	},
	contentVisible: {
		opacity: 1,
		transform: 'translateY(0)',
	},
	description: {
		maxWidth: '400px',
	},
	greeting: {
		maxWidth: '400px',
		overflowWrap: 'break-word',
		hyphens: 'auto',
	},
});

type Props = {
	onComplete?: () => void;
};

export const WelcomeScreen: (props: Props) => JSX.Element = ({
	onComplete,
}: Props): JSX.Element => {
	const [isContentVisible, setIsContentVisible] = useState(false);
	const [userName, setUserName] = useState<string | undefined>(undefined);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { sourceComponent, sourceContext } = useModalContext();

	useEffect(() => {
		(async () => {
			const name = await fetchUserName();
			setUserName(name);
			createAnalyticsEvent({
				eventType: 'track',
				action: 'completed',
				actionSubject: 'userNameFetch',
				attributes: {
					hasName: !!name,
					sourceComponent,
					sourceContext,
				},
			}).fire('growth');
		})();
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalWelcomeScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	const handleAnimationComplete = useCallback(() => {
		setIsContentVisible(true);
		// Auto-complete after 3 seconds
		if (onComplete) {
			setTimeout(onComplete, 3000);
		}
	}, [onComplete]);

	return (
		<ScreenLayout testId="welcome-screen">
			<WelcomeAnimation onComplete={handleAnimationComplete} />
			<ScreenLayout.Content>
				<Stack xcss={cx(styles.content, isContentVisible && styles.contentVisible)}>
					{userName && (
						<Box xcss={styles.greeting}>
							<Heading size="large">
								<FormattedMessage {...messages.greeting} values={{ name: userName }} />
							</Heading>
						</Box>
					)}
					<Heading size="large">
						<FormattedMessage {...messages.heading} />
					</Heading>

					<Box xcss={styles.description}>
						<Text size="large" weight="regular" color="color.text.subtlest">
							<FormattedMessage {...messages.description} />
						</Text>
					</Box>
				</Stack>
			</ScreenLayout.Content>
		</ScreenLayout>
	);
};
